import {
    RiArrowDownSLine,
    RiArrowRightLine,
    RiArrowRightSLine,
    RiAttachment2, RiBold, RiChat4Line, RiCodeBlock, RiCodeSSlashFill,
    RiEditLine, RiH1, RiH2, RiItalic, RiLinksFill, RiListOrdered, RiListUnordered,
    RiQuoteText,
    RiSparkling2Line, RiStrikethrough,
} from "@remixicon/react";
import React, { MutableRefObject, ReactNode } from "react";
// eslint-disable-next-line import/no-extraneous-dependencies
import ReactQuill from "react-quill";
import { TicketMessageArticleInput, TicketMessageType, useGetAiPromptLazyQuery } from "../../../__generated__/api";
import AnswerFinder from "../../agent/answer-finder/AnswerFinder";
import { LoadingIcon } from "../../loading/Loading";
import OverflowMenu from "../../overflow-menu/OverflowMenu";
import Button from "../../theme/button/Button";
import Dropdown from "../../theme/dropdown/Dropdown";
import Styles from "./Editor.module.scss";
import createLinkButton from "./Link";
import Variables, { VariablesInput } from "./Variables";
import useChangingCallback from "../../../use-changing-callback";
import Icon from "../../theme/icon/Icon";
import usePortal from "../../../contexts/PortalContext";
import { LoggingHandler } from "../../../contexts/logging/LoggingContext";

interface ToolbarProps {
    allowAI?: boolean;
    id: string;
    sendButton?: ReactNode;

    isAllowHeaders?: boolean;

    variables?: VariablesInput;
    quillRef?: MutableRefObject<ReactQuill | null>;

    canFormat?: boolean;
    type?: TicketMessageType;
    onTypeChange?: (type: TicketMessageType) => void;
    onAttachmentClicked: (() => void) | undefined;
    onArticleSelected?: (articles: TicketMessageArticleInput) => void;
    onCannedMessage: (cannedMessage: string | undefined) => void;
    onReplaceText: (text: string) => void;

    metricPathPrefix?: string[];
}

type Quill = InstanceType<(typeof ReactQuill)["Quill"]>;

function createToggleFormat(name: string) {
    // eslint-disable-next-line func-names
    return function (this: {quill: Quill}) {
        const position = this.quill.getSelection();
        if (!position) {
            return;
        }
        const current = this.quill.getFormat(position)[name];
        this.quill.formatText(position, name, !current);
    };
}

function createListToggle(name: string, type?: string) {
    // eslint-disable-next-line func-names
    return function (this: {quill: Quill}) {
        this.quill.format(name, type);
    };
}

function createBlock(name: string, value?: unknown) {
    // eslint-disable-next-line func-names
    return function (this: {quill: Quill}) {
        const position = this.quill.getSelection();
        if (!position) {
            return;
        }
        const current = this.quill.getFormat(position)[name];
        let newValue: unknown = !current;
        if (value != null) {
            newValue = current === value ? false : value;
        }
        this.quill.format(name, newValue);
    };
}

export const MODULES = {
    customBold: createToggleFormat("bold"),
    customItalic: createToggleFormat("italic"),
    customStrike: createToggleFormat("strike"),
    customCode: createToggleFormat("code"),
    customCodeBlock: createBlock("code-block"),
    customBlockquote: createBlock("blockquote"),
    customHeaderH1: createBlock("header", 1),
    customHeaderH2: createBlock("header", 2),

    customUnorderedList: createListToggle("list", "unordered"),
    customOrderedList: createListToggle("list", "ordered"),

    customLinkButton: createLinkButton(),

};

const AI_PROMPTS = [
    {
        id: "improve",
        name: "Improve Writing",
        prompt: "Improve the writing of the provided user text. Make it more professional and clear.",
    },
    {
        id: "fix-spelling",
        name: "Fix Spelling and Grammar",
        prompt: "Fix the spelling and grammar of the provided user text.",
    },
    {
        id: "translate",
        name: "Translate",
        children: [
            "English", "Koran", "Chinese, Simplified", "Japanese", "Spanish",
            "Russian", "French", "Portuguese", "German", "Italian", "Dutch",
            "Indonesian", "Filipino", "Vietnamese",
        ].map(language => ({
            id: language,
            name: language,
            prompt: `Translate the text to ${language}.`,
        })),
    },
    {
        id: "make-longer",
        name: "Make Longer",
        prompt: "Make the text longer and more detailed.",
    },
    {
        id: "make-shorter",
        name: "Make Shorter",
        prompt: "Make the text shorter but retaining detail and tone.",
    },
    {
        id: "change-tone",
        name: "Tone",
        children: [
            "Professional", "Friendly", "Casual", "Formal", "Confident", "Straightforward",
        ].map(tone => ({
            id: tone,
            name: tone,
            prompt: `Change the tone of the provided text to ${tone}.`,
        })),
    },
];

type AiPrompt = typeof AI_PROMPTS[number];

function AIButton({ onReplaceText, quillRef, metricPathPrefix }: {
    onReplaceText: (text: string) => void;
    quillRef: ReactQuill | null | undefined
    metricPathPrefix: string[] | undefined
}) {
    const [isLoading, setIsLoading] = React.useState(false);

    const [query] = useGetAiPromptLazyQuery();

    const loadPrompt = useChangingCallback(async (prompt: AiPrompt) => {
        setIsLoading(true);
        try {
            const input = quillRef?.editor?.getText();
            if (!input) {
                return;
            }
            const result = await query({
                variables: {
                    options: {
                        prompt: `${prompt.prompt} Don't return anything else other than requested text.`,
                        input,
                    },
                },
            });
            if (result.data?.artificialIntelligencePrompt?.response) {
                onReplaceText(result.data?.artificialIntelligencePrompt?.response);
            }
        } finally {
            setIsLoading(false);
        }
    }, [quillRef?.editor]);

    if (isLoading) {
        return <LoadingIcon size="10px" />;
    }

    const createPath = (path: string[]) => (metricPathPrefix ? [...metricPathPrefix, ...path] : undefined);

    return (
        <Dropdown>
            <Dropdown.Toggle
                prefixIcon={<RiSparkling2Line />}
                className="toolbar-action"
                size="small"
                variant="ghost-gray"
            />
            <Dropdown.Menu flip>

                {AI_PROMPTS.map(prompt => (
                    prompt.children ? (
                        <Dropdown align="start" drop="end">
                            <Dropdown.Toggle
                                prefixIcon={<Icon.ArtificialIntelligence variant="default" />}
                                size="small"
                                variant="ghost-gray"
                                className={Styles["dropdown-child"]}
                                postfixIcon={<RiArrowRightSLine />}
                            >
                                <span>
                                    {prompt.name}
                                </span>
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                                {prompt.children.map(subPrompt => (
                                    <Dropdown.Item
                                        key={prompt.id}
                                        iconSize="small"
                                        onClick={() => loadPrompt(subPrompt)}
                                        metricPath={createPath(["ai-prompt", subPrompt.id])}
                                    >
                                        {subPrompt.name}
                                    </Dropdown.Item>
                                ))}
                            </Dropdown.Menu>
                        </Dropdown>
                    ) : (
                        <Dropdown.Item
                            key={prompt.id}
                            iconSize="small"
                            prefixIcon={<Icon.ArtificialIntelligence variant="default" />}
                            onClick={() => loadPrompt(prompt)}
                            metricPath={createPath(["ai-prompt", prompt.id])}
                        >
                            {prompt.name}
                        </Dropdown.Item>
                    )

                ))}
            </Dropdown.Menu>
        </Dropdown>
    );
}

export default function Toolbar({
    allowAI,
    id,
    type,
    sendButton,
    onTypeChange,
    onAttachmentClicked,
    onCannedMessage,
    onReplaceText,
    onArticleSelected,
    variables,
    quillRef,
    canFormat,
    isAllowHeaders,
    metricPathPrefix,
}: ToolbarProps) {
    const { entitlementIds } = usePortal();

    const metricPath = metricPathPrefix ? [...metricPathPrefix, "toolbar"] : ["toolbar"];

    const createPath = (path: string[]) => (metricPath ? [...metricPath, ...path] : undefined);

    return (
        <div className={Styles.toolbar}>
            {onTypeChange && (
                <Dropdown className="type-selector">
                    <Dropdown.Toggle
                        variant="filter"
                        prefixIcon={<RiChat4Line />}
                        postfixIcon={<RiArrowDownSLine />}
                    >
                        {type === TicketMessageType.AgentMessage ? "Reply" : "Note"}
                    </Dropdown.Toggle>
                    <Dropdown.Menu flip>
                        <Dropdown.Item
                            iconSize="small"
                            prefixIcon={<RiChat4Line />}
                            metricPath={createPath(["message"])}
                            onClick={() => onTypeChange(TicketMessageType.AgentMessage)}
                        >
                            Reply
                        </Dropdown.Item>
                        <Dropdown.Item
                            iconSize="small"
                            prefixIcon={<RiEditLine />}
                            metricPath={createPath(["note"])}
                            onClick={() => onTypeChange(TicketMessageType.Comment)}
                        >
                            Note
                        </Dropdown.Item>
                    </Dropdown.Menu>
                </Dropdown>
            )}

            {variables && quillRef && (
                <Variables quillRef={quillRef} variables={variables} />
            )}

            {onArticleSelected && (
                <AnswerFinder
                    onAnswerSelected={metricPath
                        ? LoggingHandler.instance.wrapMetric([...metricPath, "answer-selected"], onArticleSelected)
                        : onArticleSelected}
                    onCannedMessageSelected={metricPath
                        ? LoggingHandler.instance.wrapMetric([...metricPath, "canned-mesasge-selected"], onCannedMessage)
                        : onCannedMessage}
                />

            )}
            {onAttachmentClicked && (
                <Button
                    prefixIcon={<RiAttachment2 />}
                    size="small"
                    className="toolbar-action"
                    variant="ghost-gray"
                    onClick={onAttachmentClicked}
                    metricPath={createPath(["attachment"])}
                />
            )}
            {allowAI && entitlementIds.has(entitlementIds.keys.ARTIFICIAL_INTELLIGENCE) && (
                <AIButton onReplaceText={onReplaceText} quillRef={quillRef?.current} metricPathPrefix={metricPathPrefix} />
            )}

            {canFormat && (
                <OverflowMenu
                    id={id}
                    className={`formatting-options ${Styles["overflow-container"]}`}
                    toggleSettings={{ size: "small", variant: "ghost-gray", title: "Formatting Options" }}
                >
                    <Button
                        prefixIcon={<RiBold />}
                        size="small"
                        title="Bold"
                        variant="ghost-gray"
                        className="toolbar-action ql-customBold"
                        metricPath={createPath(["bold"])}
                    />
                    <Button
                        prefixIcon={<RiItalic />}
                        size="small"
                        title="Italic"
                        variant="ghost-gray"
                        className="toolbar-action ql-customItalic"
                        metricPath={createPath(["italic"])}
                    />
                    <Button
                        prefixIcon={<RiStrikethrough />}
                        size="small"
                        title="Strikethrough"
                        variant="ghost-gray"
                        className="toolbar-action ql-customStrike"
                        metricPath={createPath(["strikethrough"])}
                    />
                    <Button
                        prefixIcon={<RiCodeSSlashFill />}
                        size="small"
                        title="Code"
                        variant="ghost-gray"
                        className="toolbar-action ql-customCode"
                        metricPath={createPath(["code"])}
                    />
                    <Button
                        prefixIcon={<RiLinksFill />}
                        size="small"
                        title="Link"
                        variant="ghost-gray"
                        className="toolbar-action ql-customLinkButton"
                        metricPath={createPath(["link"])}
                    />
                    <Button
                        prefixIcon={<RiListUnordered />}
                        size="small"
                        title="Unordered List"
                        variant="ghost-gray"
                        className="toolbar-action ql-customUnorderedList"
                        metricPath={createPath(["unordered-list"])}
                    />
                    <Button
                        prefixIcon={<RiListOrdered />}
                        size="small"
                        title="Ordered List"
                        variant="ghost-gray"
                        className="toolbar-action ql-customOrderedList"
                        metricPath={createPath(["ordered-list"])}
                    />
                    <Button
                        prefixIcon={<RiQuoteText />}
                        size="small"
                        title="Blockquote"
                        variant="ghost-gray"
                        className="toolbar-action ql-customBlockquote"
                        metricPath={createPath(["quote"])}
                    />
                    <Button
                        prefixIcon={<RiCodeBlock />}
                        size="small"
                        title="Code Block"
                        variant="ghost-gray"
                        className="toolbar-action ql-customCodeBlock"
                        metricPath={createPath(["code-block"])}
                    />
                    {isAllowHeaders && (
                        <>
                            <Button
                                prefixIcon={<RiH1 />}
                                size="small"
                                title="Header (h1) - Largest Headers"
                                variant="ghost-gray"
                                className="toolbar-action ql-customHeaderH1"
                                metricPath={createPath(["h1"])}
                            />
                            <Button
                                prefixIcon={<RiH2 />}
                                size="small"
                                title="Header (h2) - 2nd Largest Headers"
                                variant="ghost-gray"
                                className="toolbar-action ql-customHeaderH2"
                                metricPath={createPath(["h2"])}
                                value="2"
                            />
                        </>
                    )}

                </OverflowMenu>
            )}

            {sendButton && (
                <div className="send-button">
                    {sendButton}
                </div>
            )}
        </div>
    );
}

Toolbar.defaultProps = {
    allowAI: false,
    type: TicketMessageType.AgentMessage,
    onTypeChange: undefined,
    sendButton: undefined,
    onArticleSelected: undefined,
    variables: undefined,
    quillRef: undefined,
    canFormat: true,
    isAllowHeaders: false,
    metricPathPrefix: undefined,
};
